import { useContext, useState } from "react";
import { BaseContext } from "../../../../../../contexts/BaseContext";
import { dataSliceService } from "../../../../../../services/api";
import { toast } from "react-hot-toast";
import { getVdbLogo } from "../../../../../../config/config";
import { Dialog } from "@mui/material";
import MetadataTable from "../../MetadataTable/MetadataTable";

const MetadataTableWrapper = () => {
  const [tableFilters, setTableFilters] = useState({});
  const [tableConditionsNew, setTableConditionsNew] = useState(null);

  return (
    <MetadataTable
      backendFilters={tableFilters}
      onFiltersChange={setTableFilters}
      source="createSlice"
      conditionsNew={tableConditionsNew}
      setConditionsNew={setTableConditionsNew}
    />
  );
};

export const DataSliceSelect = ({ onCreateNew, onDataSliceSelect }) => {
  const { deasyUserConfig, dataSlices, refreshDataSlicesHook } =
    useContext(BaseContext);
  const deasyApiKey = deasyUserConfig.deasyApiKey;
  const [showMetadataModal, setShowMetadataModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("lastUpdated"); // 'name', 'lastUpdated', 'dataPoints'

  const handleDeleteDataSlice = async (dataSliceId, e) => {
    e.stopPropagation();
    if (window.confirm("Are you sure you want to delete this data slice?")) {
      try {
        await dataSliceService.deleteDataslice(dataSliceId, deasyApiKey);
        refreshDataSlicesHook();
        toast.success("Data slice deleted successfully");
      } catch (error) {
        toast.error("Failed to delete data slice");
        console.error("Delete data slice error:", error);
      }
    }
  };

  const CreateDataSliceModal = () => (
    <Dialog
      open={showMetadataModal}
      onClose={() => setShowMetadataModal(false)}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          height: "90vh",
          maxHeight: "90vh",
          borderRadius: "12px",
        },
      }}
    >
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 text-left">
          <div className="text-left">
            <h2 className="text-xl font-semibold text-gray-900">
              Create Data Slice
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Filter and select data to create a new data slice
            </p>
          </div>
          <button
            onClick={() => setShowMetadataModal(false)}
            className="text-gray-400 hover:text-gray-500 transition-colors"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-auto p-6">
          <MetadataTableWrapper />
        </div>
      </div>
    </Dialog>
  );

  const formatSliceName = (dataSlice) => {
    // Check if it's a default slice (starts with "Deasy_Data_")
    if (dataSlice.name.startsWith("Deasy_Data_")) {
      const parts = dataSlice.name.split("_");
      const dbType = parts[2];
      const collection = parts.slice(3).join("_");
      return `All data from ${dbType} ${collection}`;
    }
    return dataSlice.name;
  };

  // Filter out the default slices and apply search
  const displayedDataSlices = dataSlices
    .filter((dataSlice) => !dataSlice.name.startsWith("Deasy_Data_"))
    .filter((dataSlice) => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        dataSlice.name.toLowerCase().includes(query) ||
        dataSlice.description?.toLowerCase().includes(query) ||
        dataSlice.vector_db_config?.collection_name
          ?.toLowerCase()
          .includes(query)
      );
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "name":
          return a.name.localeCompare(b.name);
        case "dataPoints":
          return b.dataPoints - a.dataPoints;
        case "lastUpdated":
        default:
          return new Date(b.lastUpdated) - new Date(a.lastUpdated);
      }
    });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="px-6 pt-2 pb-6">
      {/* Controls Section */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        {/* Search Bar */}
        <div className="flex-1 relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg
              className="h-5 w-5 text-gray-400"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <input
            type="text"
            placeholder="Search data slices..."
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Sort Dropdown */}
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
        >
          <option value="lastUpdated">Sort by Last Updated</option>
          <option value="name">Sort by Name</option>
          <option value="dataPoints">Sort by Document Count</option>
        </select>

        {/* Create Button */}
        <button
          onClick={() => setShowMetadataModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
        >
          <svg
            className="-ml-1 mr-2 h-5 w-5"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
              clipRule="evenodd"
            />
          </svg>
          Create Data Slice
        </button>
      </div>

      {/* Data Slices Grid */}
      {displayedDataSlices.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 mb-4">
            <svg
              className="h-6 w-6 text-gray-400"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchQuery
              ? "No matching data slices found"
              : "No Data Slices Yet"}
          </h3>
          <p className="text-gray-500 mb-4">
            {searchQuery
              ? "Try adjusting your search terms"
              : "Create your first data slice to start organizing your data"}
          </p>
          {!searchQuery && (
            <button
              onClick={() => setShowMetadataModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
            >
              Create your first data slice
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {displayedDataSlices.map((dataSlice) => (
            <div
              key={dataSlice.id}
              onClick={() => onDataSliceSelect(dataSlice)}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md hover:border-gray-300 transition-all cursor-pointer group"
            >
              {/* Card Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-gray-900 truncate group-hover:text-primary transition-colors">
                    {formatSliceName(dataSlice)}
                  </h3>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-700 truncate p-2">
                      {dataSlice.vector_db_config?.collection_name ||
                        "Unknown collection"}
                    </p>
                  </div>
                  <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                    {dataSlice.description || "No description provided"}
                  </p>
                </div>
              </div>

              {/* Data Source Info */}
              <div className="flex items-center gap-3 mb-4">
                {dataSlice.vector_db_config && (
                  <img
                    src={getVdbLogo(dataSlice.vector_db_config.type)}
                    alt={dataSlice.vector_db_config.type}
                    className="h-8 w-auto rounded"
                  />
                )}
              </div>

              {/* Stats */}
              <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
                <div className="flex items-center">
                  <svg
                    className="h-4 w-4 mr-1 text-gray-400"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <span>{dataSlice.dataPoints.toLocaleString()} documents</span>
                </div>
                <div className="flex items-center">
                  <svg
                    className="h-4 w-4 mr-1 text-gray-400"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span>{formatDate(dataSlice.lastUpdated)}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end">
                <button
                  onClick={(e) => handleDeleteDataSlice(dataSlice.id, e)}
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-red-600 bg-red-50 hover:bg-red-100 hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <CreateDataSliceModal />
    </div>
  );
};
